/**
 * BookVoiceStatsStrip.jsx
 *
 * Phase 4a — BOOK_VOICE_STATS_SPEC §6.
 *
 * Horizontal 5-cell strip summarizing a book's voice-readiness for the
 * Book Creator. Rendered in two variants:
 *
 *   - `full`    — all 5 cells; used on the VoiceCloningPipeline page above
 *                 the per-variation table (§8.2).
 *   - `compact` — last 3 cells only (Clones / Script / Coverage); used in
 *                 the BookWorksheetPage-enh header (§8.1). The worksheet
 *                 page already surfaces Characters / Variations counts
 *                 elsewhere so those cells are dropped to save real estate.
 *
 * Behaviors:
 *   - On mount / bookId-change: dispatches `fetchBookVoiceStats({ bookId })`.
 *   - The `full` variant additionally dispatches `fetchBookScriptCoverage`
 *     (compact stays DB-only per spec §8.1.2).
 *   - 30-second TTL inside the thunk itself guards tight remount loops.
 *   - Embeds <ScriptVersionSelector> inside the Script cell; switching
 *     versions re-fires coverage against the new `scriptVersion`.
 *   - Empty (brand-new book, no stats nodes at all) → prose "Import this
 *     book…" tile per §6.4.
 *   - Error → collapsed retry strip.
 *   - A11y: clickable cells are <button>, read-only cells are <div
 *     role="status">; numbers are aria-live="polite"; progress uses <progress>.
 */

import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchBookVoiceStats,
  fetchBookScriptCoverage,
  selectBookVoiceStats,
  selectBookScriptCoverage,
  selectBookVoiceStatsLoading,
  selectBookVoiceStatsError,
  selectActiveScriptVersion
} from '../../slices/voiceStatsSlice';

import ScriptVersionSelector from './ScriptVersionSelector';

import './BookVoiceStatsStrip.css';

// --- tiny helpers -----------------------------------------------------------

const pct = (num, den) => {
  if (!den || !Number.isFinite(num / den)) return 0;
  return Math.round((num / den) * 100);
};

const breakdownTitle = (t) => {
  if (!t) return '';
  const parts = [];
  if (t.variationsCloned != null) parts.push(`${t.variationsCloned} cloned`);
  if (t.variationsPending != null) parts.push(`${t.variationsPending} pending`);
  if (t.variationsFailed != null) parts.push(`${t.variationsFailed} failed`);
  if (t.variationsUnstarted != null) parts.push(`${t.variationsUnstarted} unstarted`);
  return parts.join(' · ');
};

// --- sub-components ---------------------------------------------------------

const StatCell = ({
  label,
  value,
  sub,
  tooltip,
  onClick,
  children,
  emphasis = false
}) => {
  const Tag = onClick ? 'button' : 'div';
  const commonProps = onClick
    ? { type: 'button', onClick, className: 'bvss-cell bvss-cell-clickable' }
    : { role: 'status', className: 'bvss-cell' };
  return (
    <Tag
      {...commonProps}
      title={tooltip || undefined}
      data-emphasis={emphasis ? 'true' : undefined}
    >
      <div className="bvss-cell-label">{label}</div>
      {value != null && (
        <div className="bvss-cell-value" aria-live="polite">
          {value}
        </div>
      )}
      {sub && <div className="bvss-cell-sub">{sub}</div>}
      {children}
    </Tag>
  );
};

const SkeletonCell = ({ label }) => (
  <div className="bvss-cell bvss-cell-skeleton" role="status" aria-busy="true">
    <div className="bvss-cell-label">{label}</div>
    <div className="bvss-skeleton-bar bvss-skeleton-bar-value" />
    <div className="bvss-skeleton-bar bvss-skeleton-bar-sub" />
  </div>
);

// --- main component ---------------------------------------------------------

const BookVoiceStatsStrip = ({
  bookId,
  scriptVersionUuid,
  variant = 'full',
  onCharacterGapClick,
  onScriptVersionClick,
  onOpenCoverage,
  className = ''
}) => {
  const dispatch = useDispatch();

  const stats = useSelector((s) => selectBookVoiceStats(s, bookId));
  const coverage = useSelector((s) => selectBookScriptCoverage(s, bookId));
  const loading = useSelector((s) => selectBookVoiceStatsLoading(s, bookId));
  const error = useSelector((s) => selectBookVoiceStatsError(s, bookId));
  const activeVersion = useSelector((s) => selectActiveScriptVersion(s, bookId));

  const isFull = variant === 'full';
  const svUuid = scriptVersionUuid || activeVersion?.uuid || null;

  // ---- fetch orchestration (30s TTL is enforced inside the thunks) --------
  useEffect(() => {
    if (!bookId) return;
    dispatch(
      fetchBookVoiceStats({
        bookId,
        include: isFull ? 'totals,perCharacter' : 'totals'
      })
    );
  }, [dispatch, bookId, isFull]);

  useEffect(() => {
    if (!bookId || !isFull) return;
    dispatch(fetchBookScriptCoverage({ bookId, scriptVersion: svUuid || undefined }));
  }, [dispatch, bookId, isFull, svUuid]);

  const handleRetry = useCallback(() => {
    if (!bookId) return;
    dispatch(fetchBookVoiceStats({ bookId, force: true }));
    if (isFull) {
      dispatch(fetchBookScriptCoverage({ bookId, scriptVersion: svUuid || undefined, force: true }));
    }
  }, [dispatch, bookId, isFull, svUuid]);

  // ---- derive display values ---------------------------------------------
  const totals = stats?.totals || null;
  const jsonSummary = coverage?.jsonSummary || null;
  const resolution = coverage?.characterResolution || [];

  const coverageCounts = useMemo(() => {
    if (!coverage) return { resolved: 0, total: 0, gaps: 0 };
    const total = jsonSummary?.characterCount ?? resolution.length;
    const resolved = resolution.filter((r) => r.dbCharacterUuid).length;
    const gaps = (coverage.gaps?.speakersMissingInDb || []).length;
    return { resolved, total, gaps };
  }, [coverage, jsonSummary, resolution]);

  // ---- error state --------------------------------------------------------
  const hasError = !!(error?.stats || (isFull && error?.coverage));
  if (hasError && !stats && !coverage) {
    return (
      <div className={`bvss-strip bvss-strip-error ${className}`} role="alert">
        <span>Couldn't load voice stats.</span>
        <button type="button" className="bvss-retry-btn" onClick={handleRetry}>
          Retry
        </button>
      </div>
    );
  }

  // ---- loading state ------------------------------------------------------
  const isLoading = (loading?.stats && !stats) || (isFull && loading?.coverage && !coverage);
  if (isLoading) {
    return (
      <div className={`bvss-strip bvss-strip-${variant} ${className}`} aria-busy="true">
        {isFull && <SkeletonCell label="Characters" />}
        {isFull && <SkeletonCell label="Variations" />}
        <SkeletonCell label="Clones" />
        <SkeletonCell label="Script" />
        <SkeletonCell label="Coverage" />
      </div>
    );
  }

  // ---- zero / not-imported state -----------------------------------------
  const isZero =
    stats &&
    (!totals ||
      ((totals.characters | 0) === 0 &&
        (totals.variations | 0) === 0 &&
        (totals.sampleSnippets | 0) === 0));
  if (isZero) {
    return (
      <div className={`bvss-strip bvss-strip-zero ${className}`} role="status">
        <div className="bvss-zero-tile">
          <strong>No voice data yet.</strong>{' '}
          Import this book to begin voice preparation. Once samples are
          approved and cloned, readiness stats will populate here.
        </div>
      </div>
    );
  }

  // ---- healthy state ------------------------------------------------------
  const clonedPct = pct(totals?.variationsCloned || 0, totals?.variations || 0);
  const tooltipVariations = breakdownTitle(totals);

  return (
    <div className={`bvss-strip bvss-strip-${variant} ${className}`}>
      {isFull && (
        <StatCell
          label="Characters"
          value={totals?.characters ?? 0}
          sub={totals?.characters === 1 ? 'distinct' : 'distinct'}
        />
      )}
      {isFull && (
        <StatCell
          label="Variations"
          value={totals?.variations ?? 0}
          sub={tooltipVariations}
          tooltip={tooltipVariations}
        />
      )}

      {/* Clones */}
      <StatCell
        label="Clones"
        value={`${totals?.variationsCloned ?? 0}/${totals?.variations ?? 0}`}
        sub={`${clonedPct}%`}
        tooltip={tooltipVariations}
      >
        <progress
          className="bvss-progress"
          max={totals?.variations || 1}
          value={totals?.variationsCloned || 0}
          aria-valuenow={totals?.variationsCloned || 0}
          aria-valuemax={totals?.variations || 1}
          aria-label={`Cloned ${totals?.variationsCloned ?? 0} of ${totals?.variations ?? 0}`}
        />
      </StatCell>

      {/* Script — embeds the selector when we have a bookId. `onScriptVersionClick`
          is honored as an escape hatch for a modal picker if ever needed. */}
      <div
        className="bvss-cell bvss-cell-script"
        role={onScriptVersionClick ? undefined : 'status'}
      >
        <div className="bvss-cell-label">Script</div>
        {onScriptVersionClick ? (
          <button
            type="button"
            className="bvss-script-button"
            onClick={onScriptVersionClick}
          >
            {activeVersion
              ? `v${activeVersion.version || '?'} · ${activeVersion.fileName || '—'}`
              : 'Pick version…'}
          </button>
        ) : (
          <ScriptVersionSelector bookId={bookId} dense={!isFull} />
        )}
      </div>

      {/* Coverage */}
      <StatCell
        label="Coverage"
        value={
          coverage
            ? coverageCounts.total > 0
              ? `${coverageCounts.resolved}/${coverageCounts.total}`
              : '—'
            : isFull
              ? '…'
              : '—'
        }
        sub={
          coverage
            ? coverageCounts.gaps === 0
              ? '✓ all speakers present'
              : `${coverageCounts.gaps} gap${coverageCounts.gaps === 1 ? '' : 's'}`
            : isFull
              ? 'loading…'
              : 'open pipeline'
        }
        tooltip={
          coverage?.gaps?.speakersMissingInDb?.length
            ? `Missing: ${coverage.gaps.speakersMissingInDb.slice(0, 5).join(', ')}${coverage.gaps.speakersMissingInDb.length > 5 ? '…' : ''
            }`
            : undefined
        }
        onClick={
          isFull && (onOpenCoverage || onCharacterGapClick)
            ? () => {
              if (onOpenCoverage) onOpenCoverage(coverage);
              // If only onCharacterGapClick was provided, focus the first gap.
              const firstGap = coverage?.gaps?.speakersMissingInDb?.[0];
              if (firstGap && onCharacterGapClick) onCharacterGapClick(firstGap);
            }
            : undefined
        }
        emphasis={coverage && coverageCounts.gaps > 0}
      />
    </div>
  );
};

BookVoiceStatsStrip.propTypes = {
  bookId: PropTypes.string.isRequired,
  scriptVersionUuid: PropTypes.string,
  variant: PropTypes.oneOf(['full', 'compact']),
  onCharacterGapClick: PropTypes.func,
  onScriptVersionClick: PropTypes.func,
  onOpenCoverage: PropTypes.func,
  className: PropTypes.string
};

export default BookVoiceStatsStrip;
